import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { useProfile } from '../../../hooks/useProfile';
import {
  ACCOUNT_INVOICE_SENDER,
  defaultInvoiceEmail,
  sendInvoiceEmail,
  sendsOnBehalf,
  signedInMailbox,
  validateInvoiceEmail,
  type InvoiceEmailDraft,
} from '../accountInvoiceEmail';
import { accountInvoicesService, type LoadedAccountInvoice } from '../accountInvoices.service';

/** Emails an account invoice to the account with its PDF attached, from the Keystone South UK mailbox. */
export function SendInvoiceModal({
  invoiceId,
  open,
  onClose,
  onSent,
}: {
  invoiceId: string;
  open: boolean;
  onClose: () => void;
  onSent: (message: string) => void;
}) {
  const { profile } = useProfile();
  const [loaded, setLoaded] = useState<LoadedAccountInvoice | null>(null);
  const [draft, setDraft] = useState<InvoiceEmailDraft>({ to: '', cc: '', subject: '', body: '' });
  const [markSent, setMarkSent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mailbox = signedInMailbox();

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(true);
    accountInvoicesService.load(invoiceId)
      .then((next) => {
        setLoaded(next);
        setMarkSent(next.invoice.status === 'draft');
        setDraft(defaultInvoiceEmail({
          invoice: next.invoice,
          account: next.account,
          companyName: next.config?.companyName || ACCOUNT_INVOICE_SENDER.name,
          bank: {
            name: next.config?.bankName,
            accountName: next.config?.bankAccountName,
            sortCode: next.config?.bankSortCode,
            accountNumber: next.config?.bankAccountNumber,
          },
          senderName: profile?.full_name || null,
        }));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the invoice'))
      .finally(() => setLoading(false));
    // profile name only seeds the sign-off
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId]);

  const set = (key: keyof InvoiceEmailDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDraft((current) => ({ ...current, [key]: event.target.value }));
  };

  const send = async () => {
    if (!loaded) return;
    const problem = validateInvoiceEmail(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const pdf = await accountInvoicesService.pdfBlob(loaded);
      await sendInvoiceEmail({ draft, pdf, fileName: accountInvoicesService.pdfFileName(loaded) });
      let note = `${loaded.invoice.invoiceRef} emailed to ${draft.to}.`;
      if (markSent && loaded.invoice.status === 'draft') {
        try {
          await accountInvoicesService.setStatus(loaded.invoice.id, 'sent');
          note += ' Marked as sent.';
        } catch (statusError) {
          note += ` The email went, but it could not be marked as sent: ${statusError instanceof Error ? statusError.message : 'unknown error'}`;
        }
      }
      onSent(note);
    } catch (err) {
      const message = err instanceof Error ? err.message : String((err as any)?.message || err);
      setError(/send ?as|sendas|ErrorSendAsDenied|not authorized|access ?denied/i.test(message)
        ? `Microsoft refused to send from ${ACCOUNT_INVOICE_SENDER.address}. Sign in to JARVIS’s Microsoft connection as that mailbox, or give your account Send As permission for it. (${message})`
        : message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !sending && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 620 }, maxHeight: '92vh', overflow: 'auto' }}>
        <DialogTitle>Send {loaded?.invoice.invoiceRef || 'invoice'} to customer</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={1.5}>
              <Typography level="body-sm" textColor="neutral.600">
                From {ACCOUNT_INVOICE_SENDER.name} &lt;{ACCOUNT_INVOICE_SENDER.address}&gt;, with the invoice PDF attached.
              </Typography>
              {sendsOnBehalf(mailbox) && (
                <Alert color="warning" variant="soft">
                  You are signed in to Microsoft as {mailbox}. Sending from {ACCOUNT_INVOICE_SENDER.address} only works if your account has Send As permission for it.
                </Alert>
              )}
              <FormControl required>
                <FormLabel>To</FormLabel>
                <Input value={draft.to} onChange={set('to')} placeholder="accounts@example.co.uk" />
                {!draft.to && <FormHelperText>This account has no email address. Add one in Directory → Accounts, or type it here.</FormHelperText>}
              </FormControl>
              <FormControl>
                <FormLabel>CC</FormLabel>
                <Input value={draft.cc} onChange={set('cc')} placeholder="Separate several with ;" />
              </FormControl>
              <FormControl required>
                <FormLabel>Subject</FormLabel>
                <Input value={draft.subject} onChange={set('subject')} />
              </FormControl>
              <FormControl required>
                <FormLabel>Message</FormLabel>
                <Textarea minRows={9} value={draft.body} onChange={set('body')} />
              </FormControl>
              {loaded?.invoice.status === 'draft' && (
                <Checkbox
                  checked={markSent}
                  onChange={(event) => setMarkSent(event.target.checked)}
                  label={loaded.invoice.freeagentInvoiceUrl
                    ? 'Mark as sent once emailed'
                    : 'Mark as sent once emailed (also creates it in FreeAgent if sync is on)'}
                />
              )}
              {error && <Alert color="danger">{error}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={send} loading={sending} disabled={loading || !loaded}>Send email</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={sending}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
